import * as Sentry from '@sentry/react';

type ReportLevel = 'error' | 'warning' | 'info';

export interface ReportContext {
  /** Owning component or module; becomes the `component` tag. */
  component: string;
  level?: ReportLevel;
  /** Low-cardinality values only (indexed and filterable in Sentry). */
  tags?: Record<string, string>;
  /**
   * Debug data (not indexed). Reports are anonymous by default: only add personal data (e.g. an email)
   * when that value is itself the cause of the problem.
   */
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

export interface UnhandledContentReportOptions {
  component: string;
  /** Short, stable identifier used for grouping, e.g. `unsupported-inline-child`. */
  reason: string;
  /** Low-cardinality detail that splits the grouping, e.g. `figure.leftAlign`. */
  variant?: string;
  extra?: Record<string, unknown>;
}

const sentUnhandledContentKeys = new Set<string>();

export function reportToSentry(errorOrMessage: unknown, context: ReportContext) {
  const { component, level = 'error', tags, extra, fingerprint } = context;

  if (import.meta.env.DEV) {
    console[level === 'error' ? 'error' : 'warn'](`[${component}]`, errorOrMessage, extra ?? '');
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('component', component);
    if (tags) scope.setTags(tags);
    if (extra) scope.setExtras(extra);
    if (fingerprint) scope.setFingerprint(fingerprint);

    if (errorOrMessage instanceof Error) {
      Sentry.captureException(errorOrMessage);
    } else {
      Sentry.captureMessage(String(errorOrMessage));
    }
  });
}

// Reported once per component/reason/variant/page per session, so re-renders and repeated occurrences don't flood Sentry.
export function reportUnhandledContent({ component, reason, variant = '', extra }: UnhandledContentReportOptions) {
  const path = window.location.pathname;
  const key = [component, reason, variant, path].join('|');
  if (sentUnhandledContentKeys.has(key)) return;
  sentUnhandledContentKeys.add(key);

  reportToSentry(`Unhandled content: ${reason}`, {
    component,
    level: 'warning',
    tags: { issueType: 'unhandled-content', reason },
    extra: { ...extra, variant, path, appId: import.meta.env.VITE_APP_ID },
    fingerprint: ['unhandled-content', component, reason, variant],
  });
}

/** Test-only: clears the per-session dedupe so each test starts fresh. */
export function resetUnhandledContentDedupeForTests() {
  sentUnhandledContentKeys.clear();
}
